<template>
  <div>
    <NText variant="caption1" weight="bold" class="pr-7">
      {{ t('withdrawal.withdrawalRequest') }}
    </NText>

    <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
      <form
        autocomplete="off"
        class="bg-neutral-secondary p-4 rounded-md mt-1 w-[21.5rem]"
        @submit="handleSubmit($event, onSubmit)"
      >
        <NText
          variant="caption1"
          weight="medium"
          class="text-neutral-secondary-press block pr-3 mb-2 mt-4"
        >
          {{ t('withdrawal.bankAccount') }}
        </NText>

        <VeeField
          v-slot="{ field }"
          v-model="formModel.selectedAccountId"
          name="selectedAccountId"
          rules="required"
        >
          <div class="flex">
            <NListbox
              v-model="formModel.selectedAccountId"
              input-size="small"
              v-bind="field"
              :disabled="isDisabled || !userBankAccounts.length"
              :error="errors.selectedAccountId"
              :required-sign="false"
              required
              class="w-full"
              :placeholder="t('withdrawal.selectBankAccount')"
            >
              <NListboxOption
                v-for="(account, index) of userBankAccounts"
                :key="`${account.accountId}${index}`"
                :value="account.accountId"
                :label="account.title"
              >
                <NText weight="medium" variant="caption1">
                  {{ account.title }}
                </NText>
              </NListboxOption>
            </NListbox>

            <NPopper hover offset-y="8" placement="top">
              <div class="relative">
                <NButtonIcon
                  v-gid="{
                    module: 'cash',
                    screen: 'home',
                    section: 'withdrawalDialog',
                    component: 'addBankAccountDialog',
                  }"
                  size="small"
                  variant="flat"
                  class="mr-2"
                  type="button"
                  @click="openAddBankAccountDialog"
                >
                  <NegarPlusOutlineIcon />
                </NButtonIcon>
              </div>

              <template #content>
                <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                  <NText
                    weight="medium"
                    variant="caption2"
                    class="text-neutral-quaternary"
                  >
                    {{ t('withdrawal.createBankAccount') }}
                  </NText>
                </div>
              </template>
            </NPopper>
          </div>
        </VeeField>

        <div class="flex justify-between items-center px-3 py-2">
          <div>
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-secondary-press"
            >
              {{ t('withdrawal.withdrawalDate') }}
            </NText>
            <NLoading
              v-if="isRevalidatingPayableAmounts"
              :size="10"
              class="text-brand-primary inline-flex mx-2"
            />
          </div>

          <div>
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-secondary-press ml-1"
            >
              {{ t('withdrawal.withdrawalBalance') }}
            </NText>
            <NegarInfoWithBorderIcon
              class="inline cursor-pointer"
              size="16"
              @click="openWithdrawalBalanceDialog"
            />
          </div>
        </div>

        <NBox
          class="mb-3"
          :disabled="isDisabled || !formModel.selectedAccountId"
        >
          <VeeField
            v-slot="{ field }"
            v-model="formModel.selectedTimeSpan"
            name="selectedTimeSpan"
            rules="required"
          >
            <NRadioGroup
              v-model="formModel.selectedTimeSpan"
              v-bind="field"
              :error="errors.selectedTimeSpan"
              :required-sign="false"
              required
              class="mb-1"
            >
              <template
                v-for="(item, index) in payableAmounts"
                :key="item.tDate"
              >
                <div
                  v-if="item.isEnabled"
                  class="flex flex-col border-b p-3 last:border-none border-neutral-secondary"
                >
                  <NRadio
                    class="relative w-full"
                    :value="item.tDate"
                    :disabled="isDisabled || !formModel.selectedAccountId"
                    @click="
                      isPayFast(index)
                        ? (isPayFastSelected = true)
                        : (isPayFastSelected = false)
                    "
                  >
                    <span
                      class="flex justify-between items-center"
                      :class="{
                        'text-neutral-primary':
                          !isDisabled && formModel.selectedAccountId,
                        'text-neutral-secondary':
                          isDisabled || !formModel.selectedAccountId,
                      }"
                    >
                      <span>
                        <NText variant="caption1" weight="bold">
                          {{
                            isPayFast(index)
                              ? t('withdrawal.payFast')
                              : d(item.tDate, 'shortDate')
                          }}
                        </NText>

                        <NegarInfoWithBorderIcon
                          v-if="isPayFast(index)"
                          class="inline mr-1"
                          size="16"
                          @click.prevent.stop="openPayFastDialog"
                        />
                      </span>

                      <span>
                        <NumberFormatter
                          #="{ number }"
                          :value="item.amount > 0 ? item.amount : 0"
                          round="down"
                        >
                          <NText
                            class="align-middle pl-1"
                            weight="bold"
                            variant="caption1"
                            @click="
                              handleChangePrice(
                                item.amount > 0 ? item.amount : 0
                              )
                            "
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                        <NText
                          variant="overline"
                          class="text-neutral-secondary align-middle"
                        >
                          {{ t('misc.rial') }}
                        </NText>
                      </span>
                    </span>
                  </NRadio>
                </div>
              </template>
            </NRadioGroup>
          </VeeField>
        </NBox>

        <NBox class="flex py-1 px-3 my-3">
          <NegarErrorLightIcon
            class="text-warning-secondary ml-1 min-w-fit"
            size="16"
          />

          <NText variant="overline" class="text-right text-neutral-primary">
            <i18n-t
              scope="global"
              keypath="withdrawal.withdrawalNotificationMessage"
              tag="span"
            >
              <template #day>
                <NText variant="caption2" weight="bold">
                  {{ t('withdrawal.withdrawalNotificationMessageDay') }}
                </NText>
              </template>
            </i18n-t>
          </NText>
        </NBox>

        <VeeField
          v-slot="{ field }"
          v-model="formModel.requestWithdrawalValue"
          name="requestWithdrawalValue"
          :rules="
            maxWithdrawalAmount > MINIMUM_WITHDRAWAL_VALUE
              ? {
                  required: true,
                  between: [
                    MINIMUM_WITHDRAWAL_VALUE,
                    maxWithdrawalAmount,
                    t('withdrawal.withdrawalAmount'),
                    t('misc.rial'),
                  ],
                }
              : {
                  required: true,
                  equal: [
                    maxWithdrawalAmount,
                    t('withdrawal.withdrawalAmount'),
                    t('misc.rial'),
                    isPayFastSelected,
                  ],
                }
          "
        >
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press block pr-3 mb-2"
          >
            {{ t('withdrawal.withdrawalAmount') }}
          </NText>

          <NInput
            v-model="formModel.requestWithdrawalValue"
            v-mask="{
              alias: 'positiveNumber',
              showMaskOnHover: false,
            }"
            :placeholder="t('withdrawal.insertAmount')"
            type="tel"
            :disabled="!hasRemainingCash || !formModel.selectedTimeSpan"
            :input-class="['text-right']"
            v-bind="field"
            :error="errors.requestWithdrawalValue"
            :show-error="true"
            :required-sign="false"
            :change-value-on-mouse-wheel="false"
            dir="ltr"
            required
            @focusin="$event.target.select()"
          >
            <template #postfix>
              <NText variant="caption1" class="text-neutral-secondary">
                {{ t('misc.rial') }}
              </NText>
            </template>
          </NInput>

          <NText
            v-if="formModel.requestWithdrawalValue"
            variant="caption1"
            class="block text-center mb-4"
          >
            {{ requestWithdrawalValueWords }}
            {{ t('misc.toman') }}
          </NText>
        </VeeField>

        <div class="mt-6">
          <NButton
            v-gid="{
              module: 'cash',
              screen: 'home',
              section: 'withdrawalDialog',
              component: 'submitButton',
              disabled:
                !formModel.selectedAccountId?.value ||
                isCreatingWithdrawal ||
                !meta.valid,
            }"
            variant="fill"
            type="submit"
            color="green"
            class="w-full"
            :loading="isCreatingWithdrawal"
            :disabled="
              !formModel.selectedAccountId?.value ||
              isCreatingWithdrawal ||
              !meta.valid
            "
          >
            {{ t('withdrawal.submitRequest') }}
          </NButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { num2mixed } from 'num2persian';
import { useToast } from '@negar/core';
import {
  NegarPlusOutlineIcon,
  NegarInfoWithBorderIcon,
  NegarErrorLightIcon,
} from '@negar/icons';
import {
  CASH_QUERY_KEYS,
  MINIMUM_WITHDRAWAL_VALUE,
  PAY_FAST_INDEX,
  PAY_FAST_MAX_VALUE,
  useCreateWithdrawalQuery,
  useGetPayableAmountsQuery,
  useLiveCashQuery,
} from '@core/cash';
import { useUserBankAccountsQuery } from '@core/user';
import { useQueryClient } from '@libs/use';
import { sleep } from '@libs/utils';

import { useCashDialogs } from '@/modules/cash';

import NumberFormatter from '@/components/Number.vue';

import vMask from '@/directives/mask';

type WithdrawalFormModel = {
  selectedAccountId: { value: number | null; label: string | null } | null;
  requestWithdrawalValue: number | null;
  selectedTimeSpan: Date | null;
};

const { t, d } = useI18n();
const toast = useToast();
const {
  openPayFastDialog,
  openAddBankAccountDialog,
  openWithdrawalBalanceDialog,
} = useCashDialogs();

const queryClient = useQueryClient();

const { userBankAccounts } = useUserBankAccountsQuery({
  onSuccess() {
    if (userBankAccounts.value.length === 1) {
      formModel.selectedAccountId = {
        value: userBankAccounts.value[0]?.accountId,
        label: userBankAccounts.value[0]?.title,
      };
    }
  },
});

const { payableAmounts, isRevalidatingPayableAmounts, getPayableAmounts } =
  useGetPayableAmountsQuery();
const { remainingCash, getCash } = useLiveCashQuery();

const BASE_WITHDRAWAL_MODEL: WithdrawalFormModel = {
  selectedTimeSpan: null,
  selectedAccountId: null,
  requestWithdrawalValue: null,
};

const formModel = reactive<WithdrawalFormModel>({ ...BASE_WITHDRAWAL_MODEL });

const isPayFastSelected = ref(false);

const isDisabled = computed(() => {
  return remainingCash.value <= 0;
});

const hasRemainingCash = computed(() => {
  const payableAmount = payableAmounts.value.find(
    (item) => item.tDate === formModel.selectedTimeSpan
  );

  if (payableAmount) {
    return Number(payableAmount.amount) > 0;
  }

  return false;
});

const requestWithdrawalValueWords = computed(() => {
  return num2mixed(Math.round(Number(formModel.requestWithdrawalValue) / 10));
});

const maxWithdrawalAmount = computed(() => {
  const index = payableAmounts.value.findIndex(
    (item) => item.tDate === formModel.selectedTimeSpan
  );

  if (index > -1) {
    if (
      isPayFast(index) &&
      Number(payableAmounts.value[index].amount) > PAY_FAST_MAX_VALUE
    ) {
      return PAY_FAST_MAX_VALUE;
    }

    return Math.floor(Number(payableAmounts.value[index].amount));
  }

  return Math.floor(Number(remainingCash.value));
});

const isPayFast = (index: number) => index === PAY_FAST_INDEX;

const onSubmit = (model, { resetForm }) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_WITHDRAWAL_MODEL);

    resetForm();
  };

  createWithdrawal({
    formModel: {
      requestWithdrawalValue: formModel.requestWithdrawalValue as number,
      selectedTimeSpan: formModel.selectedTimeSpan as Date,
      selectedAccountId: formModel.selectedAccountId?.value as number,
    },
    resetFormModel,
  });
};

const { createWithdrawal, isCreatingWithdrawal } = useCreateWithdrawalQuery({
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    }
  },

  async onSuccess(response, data) {
    toast.neutral({
      message: t('withdrawal.successWithdrawalRequest'),
      duration: 3000,
    });

    await sleep(3000);

    getPayableAmounts();
    getCash();

    queryClient.invalidateQueries(CASH_QUERY_KEYS.withdrawals);

    data.resetFormModel();
  },
});

const handleChangePrice = (price: number) => {
  formModel.requestWithdrawalValue = price;
};
</script>
